import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, BarChart3 } from 'lucide-react';
import { useTerritoryStore } from '../store/territoryStore';
import { useAuthStore } from '../store/authStore';
import { StatsBar, RegionLegend, CoverageMap, TerritorySidebar, StateDetailView } from '../components/territories';

export default function TerritoryMap() {
  const {
    fetchMapData,
    fetchCoverageStats,
    mapData,
    coverageStats,
    isLoadingMap,
    isLoadingStats,
    mapError,
  } = useTerritoryStore();

  const { isAdmin } = useAuthStore();
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    fetchMapData();
    fetchCoverageStats();
  }, [fetchMapData, fetchCoverageStats]);

  const handleStateClick = (stateCode: string) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedState(stateCode);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setSelectedState(null);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // State drill-down view
  if (selectedState) {
    return (
      <div className={`p-6 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <StateDetailView stateCode={selectedState} onBack={handleBack} />
      </div>
    );
  }

  // National map view
  return (
    <div className={`p-6 flex flex-col h-full transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}>
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Territory Map</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Coverage overview by region and territory</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin() && (
            <Link
              to="/territories/analyze"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Analyze
            </Link>
          )}
          {isAdmin() && (
            <Link
              to="/territories/admin"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Settings2 className="w-4 h-4" />
              Admin
            </Link>
          )}
        </div>
      </div>

      {/* Stats row */}
      <StatsBar stats={coverageStats} isLoading={isLoadingStats} />

      {/* Region legend */}
      {mapData && (
        <RegionLegend
          regions={mapData.regions}
          selectedRegionId={selectedRegionId}
          onRegionSelect={setSelectedRegionId}
        />
      )}

      {/* Error state */}
      {mapError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700 dark:text-red-400">{mapError}</p>
        </div>
      )}

      {/* Map + sidebar */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Map — left 2/3 */}
        <div className="flex-[2] min-h-[400px]">
          {isLoadingMap && !mapData ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm flex items-center justify-center h-full">
              <div className="text-gray-400 dark:text-gray-500 text-sm">Loading map...</div>
            </div>
          ) : (
            <CoverageMap
              mapData={mapData}
              selectedRegionId={selectedRegionId}
              onRegionSelect={setSelectedRegionId}
              onStateClick={handleStateClick}
            />
          )}
        </div>

        {/* Sidebar — right 1/3 */}
        <div className="flex-1 min-h-[400px]">
          <TerritorySidebar mapData={mapData} selectedRegionId={selectedRegionId} />
        </div>
      </div>
    </div>
  );
}
